import { useBlocker } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

// Asks before leaving an editor with unsaved changes: links, Back, closing the
// tab, or the editor's own Cancel.
export function useUnsavedChanges(dirty: boolean) {
  // Set once a save lands, so its own navigation isn't stopped.
  const leaving = useRef(false);
  const blocks = () => dirty && !leaving.current;
  const blocker = useBlocker({
    shouldBlockFn: blocks,
    enableBeforeUnload: blocks,
    withResolver: true,
  });
  const [pending, setPending] = useState<(() => void) | null>(null);

  const allowLeave = () => {
    leaving.current = true;
  };

  const confirmDiscard = (action: () => void) => {
    if (!blocks()) {
      action();
      return;
    }
    setPending(() => action);
  };

  const open = blocker.status === 'blocked' || pending !== null;

  const dialog = (
    <ConfirmDialog
      open={open}
      title="Discard unsaved changes?"
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      onConfirm={() => {
        allowLeave();
        if (blocker.status === 'blocked') blocker.proceed();
        pending?.();
        setPending(null);
      }}
      onCancel={() => {
        if (blocker.status === 'blocked') blocker.reset();
        setPending(null);
      }}
    >
      <p>The changes you've made here haven't been saved.</p>
    </ConfirmDialog>
  );

  return { allowLeave, confirmDiscard, dialog };
}
